import {
  CircleCheck,
  CircleDashed,
  CircleSlash,
  LucideIcon,
} from "lucide-react";

import { AccordionTrigger } from "components/ui/accordion";
import { BadgeProps } from "components/ui/badge";
import { CaseTask, TaskStatus } from "pages/CaseDetail/types";
import { classNames, renderLongDate } from "utils/helpers";

const STATUS_STYLES: Record<
  TaskStatus,
  {
    label: string;
    icon: LucideIcon;
    iconClassName: string;
    variant: BadgeProps["variant"];
  }
> = {
  OPEN: {
    label: "To do",
    icon: CircleDashed,
    iconClassName: "bg-warning-soft text-warning",
    variant: "warning",
  },
  DONE: {
    label: "Done",
    icon: CircleCheck,
    iconClassName: "bg-success-soft text-success",
    variant: "success",
  },
  CANCELLED: {
    label: "Cancelled",
    icon: CircleSlash,
    iconClassName: "bg-muted text-muted-foreground",
    variant: "secondary",
  },
};

const describeTask = (task: CaseTask) =>
  [
    `Raised ${renderLongDate(task.createdOn)}`,
    task.resolvedAt &&
      `${
        task.status === "CANCELLED" ? "Cancelled" : "Completed"
      } ${renderLongDate(task.resolvedAt)}`,
  ]
    .filter(Boolean)
    .join(" | ");

const TaskSummary = ({ task }: { task: CaseTask }) => {
  const style = STATUS_STYLES[task.status];
  const Icon = style.icon;

  return (
    <div className="flex min-w-0 flex-1 items-center gap-4">
      <span
        aria-hidden="true"
        className={classNames(
          "flex size-9 shrink-0 items-center justify-center rounded-md",
          style.iconClassName,
        )}
      >
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 font-medium text-brand-copy-primary">
          {task.title}
        </div>
        <div className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
          <span>{describeTask(task)}</span>
          <span aria-hidden="true">|</span>
          <AccordionTrigger
            aria-label={`Details about ${task.title}`}
            className="flex-none gap-0.5 py-0 font-normal text-muted-foreground hover:text-brand-copy-primary hover:no-underline [&>svg]:size-3.5"
          >
            Details
          </AccordionTrigger>
        </div>
      </div>
    </div>
  );
};

export default TaskSummary;
